import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import i18n, { t } from 'i18next';
import { FocusTrap } from 'focus-trap-react';
import { Alert, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';
import { ReactComponent as PoweredByKodifLogo } from '../assets/powered-by-kodif.svg';
import {
	BASE_URL,
	CONTAINER_HEIGHT,
	CONTAINER_WIDTH,
	HARDCODED_USER,
	IS_DEVELOPMENT,
	AWS_WEB_SOCKET_URL,
} from '../constants';
import { convertMessage, setLastMessageAsDone } from '../utils';
import ChatCSAT from './ChatCSAT';
import ChatCloseConfirmation from './ChatCloseConfirmation';
import ChatInputBox from './ChatInputBox';
import ChatMessage from './ChatMessage';
import ChatMessageTyping from './ChatMessageTyping';
import ChatQuickReply from './ChatQuickReply';
import AlertFile from './utils/AlertFile';
import RefreshIcon from '../assets/RefreshIcon';
import CloseButton from '../assets/CloseBtn';
import MinimizeBtn from '../assets/MinimizeBtn';
import HandoffMessage from './HandoffMessage';
import useSuspendChatConnection from './hooks/useSuspendChatConnection';
import FocusTrapWithVisibilityCheck from './FocusTrapWithVisibilityCheck';
import ChatRating from './ChatRating';

import '../assets/css/fonts.css';
import '../assets/css/global.css';

const useStyles = makeStyles(_ => {
	return {
		chatContainer: {
			display: 'flex',
			flexDirection: 'column',
			position: 'relative',
			width: `${CONTAINER_WIDTH}px`,
			height: `${CONTAINER_HEIGHT}px`,
			boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15), 0px 2px 4px rgba(27, 36, 44, 0.1)',
			borderRadius: '16px',
			background: '#FFF',
			justifyContent: 'flex-end',
			overflow: 'hidden'
		},
		chatContainerMobile: {
			display: 'flex',
			flexDirection: 'column',
			position: 'fixed',
			top: '0',
			left: '0',
			width: '100vw',
			height: '100vh',
			boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15), 0px 2px 4px rgba(27, 36, 44, 0.1)',
			borderRadius: '1px',
			zIndex: '11',
			background: '#FFF'
		},
		chatContainerPlayground: {
			display: 'flex',
			flexDirection: 'column',
			position: 'relative',
			width: '100%',
			height: '100%',
			borderRadius: '0',
			background: '#A6823C'
		},
		chatHeader: {
			height: '58px',
			padding: '9px 12px',
			flex: '0 0 0',
			display: 'flex',
			justifyContent: 'space-between'
		},
		chartHeaderLogoContainer: {
			alignItems: 'center',
			display: 'flex',
			justifyContent: 'center',
			cursor: 'pointer',
			minHeight: '45px',
			overflow: 'hidden',
			'&:focus': {
				outline: 'none',
				boxShadow: 'none',
			},
		},
		chartHeaderRefreshIcon: {
			alignItems: 'center',
			display: 'flex',
			justifyContent: 'center',
			cursor: 'pointer',
			margin: '0 0 0 auto',
			padding: 0,
			background: 'transparent',
			border: 0,
			minHeight: '35px',
			minWidth: '35px',
			maxHeight: '35px',
			overflow: 'hidden',
			'&:hover': {
				backgroundColor: 'rgba(0, 0, 0, 0.125)',
				borderRadius: '50%'
			}
		},
		chartHeaderMinimize: {
			background: 'none',
			border: 'none',
			alignItems: 'center',
			display: 'flex',
			justifyContent: 'center',
			cursor: 'pointer',
			minHeight: '35px',
			minWidth: '35px',
			maxHeight: '35px',
			overflow: 'hidden',
			'&:hover': {
				backgroundColor: 'rgba(0, 0, 0, 0.125)',
				borderRadius: '50%'
			}
		},
		chatLoading: {
			width: '50px',
			margin: 'auto',
			padding: '1px',
			aspectRatio: '1',
			display: 'flex',
			alignItems: 'center',
			maxHeight: '50px',
			justifyContent: 'center',
			color: '#FFFFFF',
			borderRadius: '50%',
			background: '#25B09B',
			'--_m': 'conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box',
			WebkitMask: 'var(--_m)',
			mask: 'var(--_m)',
			WebkitMaskComposite: 'source-out',
			maskComposite: 'subtract',
			animation: '$l3 1s infinite linear'
		},
		'@keyframes l3': {
			to: { transform: 'rotate(1turn)' }
		},
		chatContentContainer: {
			width: '100%',
			height: 'calc(100%)',
			boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
			background: '#FFFFFF',
			display: 'flex',
			flexDirection: 'column',
			alignSelf: 'flex-end'
		},
		chatContentContainerPlayground: {
			width: '100%',
			height: 'calc(100% - 92px)',
			background: '#FFFFFF',
			borderRadius: '0px 0px 0 0',
			boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
			display: 'flex',
			flexDirection: 'column',
			alignSelf: 'flex-end'
		},
		chatContent: {
			width: '100%',
			height: '100%',
			overflowY: 'auto',
			overflowX: 'hidden',
			padding: '10px',
			boxSizing: 'border-box',
			display: 'flex',
			flexDirection: 'column',
			alignSelf: 'flex-end'
		},
		chatContentPlayground: {
			width: '100%',
			height: '100%',
			overflowY: 'auto',
			padding: '10px',
			display: 'flex',
			flexDirection: 'column',
			boxSizing: 'border-box',
			position: 'relative'
		},
		quickRepliesBoxContainer: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'center',
			alignItems: 'center',
			padding: '25px 10px 5px',
			gap: '5px',
			position: 'relative',
			bottom: '1%',
			width: '100%',
			background: '#FFFFFF',
			flexWrap: 'wrap',
			marginTop: 'auto',
			boxSizing: 'border-box'
		},
		quickRepliesBoxContainerMini: {
			display: 'inline-flex',
			alignItems: 'center',
			padding: '10px',
			gap: '4px',
			position: 'relative',
			width: '95%',
			background: '#FFFFFF',
			bottom: '1%',
			flexWrap: 'wrap',
			justifyContent: 'center',
			boxSizing: 'border-box',
			marginTop: 'auto'
		},
		chatBoxFooter: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'center',
			alignItems: 'center',
			padding: '0px',
			gap: '18px',
			position: 'relative',
			width: '100%',
			height: '30px'
			// background: '#E7E6E4',
			// boxShadow: '0px -1px 4px rgba(0, 0, 0, 0.1)',
			// borderRadius: '0px 0px 8px 8px',
		},
		chatBoxFooterPlayground: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'center',
			alignItems: 'center',
			padding: '0px',
			gap: '18px',
			position: 'absolute',
			width: '100%',
			height: '30px',
			background: '#FFFFFF',
			bottom: 0,
			// boxShadow: '0px -1px 4px rgba(0, 0, 0, 0.1)',
			borderRadius: '0px 0px 0px 0px'
		},
		chatBoxFooterText: {
			width: '56px',
			height: '13px',
			fontFamily: '\'Inter\'',
			fontStyle: 'normal',
			fontWeight: '400',
			fontSize: '10px',
			lineHeight: '130%',
			display: 'flex',
			alignItems: 'center',
			color: 'rgba(0, 0, 0, 0.33)'
		},
		fileMessageInfo: {
			justifyContent: 'center',
			margin: '10px 0',
			'& div': {
				padding: 0,
				fontSize: '12px'
			}
		},
		errorMessage: {
			boxSizing: 'border-box',
			border: '1 px solid #D8000C',
			padding: '10px',
			color: '#D8000C',
			backgroundColor: '#FFBABA',
			position: 'absolute',
			height: '60px',
			width: '100%',
			bottom: '30px',
			left: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 10,
			fontFamily: 'Inter',
			fontSize: '14px'
		}
	};
});

const DEFAULT_USER_MESSAGE_STATE = {
	type: 'user',
	senderName: 'You',
	time: null,
	value: null
};

const DEFAULT_USER_MESSAGE_FILE_STATE = {
	messageType: 'attachment',
	type: 'user',
	senderName: 'You',
	time: null,
	value: null,
	encodedAttachment: null
};

const HANDOFF_TYPES = [
	'GORGIAS_HANDOFF',
	'ZENDESK_HANDOFF',
	'SALESFORCE_HANDOFF',
	'GLADLY_HANDOFF',
	'INTERCOM_HANDOFF',
	'DIXA_HANDOFF'
];
const SESSION_EXPIRED_TEXT = 'Session ended';


/**
 * Adjusts the chat widget container height based on the visibility of the header, footer, or both.
 */
const adjustChatContainerHeight = (isPlayground, hideHeader, hideFooter) => {
	if (isPlayground) {
		// if on playground
		if (hideHeader && hideFooter) {
			// if both header and footer are hidden
			return '100%';
		} else if (hideHeader) {
			// if only header is hidden
			return 'calc(100% - 30px)';
		} else if (hideFooter) {
			// if only footer is hidden
			return 'calc(100% - 62px)';
		}
		return 'calc(100% - 92px)';
	} else {
		// if not on playground
		return hideHeader ? '100%' : 'calc(100% - 62px)';
	}
};

export default function ChatContainer({
	onExpandChange,
	appID,
	playground,
	companyConfig,
	appPosition,
	hideFooter,
	hideHeader,
	extension,
	flowVersionUuid,
	isMobile,
	onCustomHandOff,
	maxHeight,
	cookies,
	policyId,
	isTestWidget,
	policyTestUserEmail,
	isExpanded,
	isAutopilot,
	isChatWidgetVisible,
	disableLogs,
	pageSpecificData,
}) {
	const containerHeight = adjustChatContainerHeight(playground, hideHeader, hideFooter);
	const textInputRef = useRef(null);
	const fileUploadRef = useRef(null);
	const chatWindowRef = useRef(null);
	const quickRepliesContainerRef = useRef(null);

	const [conversationId, setConversationId] = useState(null);
	const [chatMessages, setChatMessages] = useState([]);
	const [userMessage, setUserMessage] = useState('');
	const [userUpdFile, setUserUpdFile] = useState('');
	const [submitType, setSubmitType] = useState('text');
	const [showUploadButton, setShowUploadButton] = useState(!!isAutopilot);
	const [showPreviewImg, setShowPreviewImg] = useState(false);
	const [userFileMessage, setUserFileMessage] = useState('');
	const [isCloseMenuActive, setIsCloseMenuActive] = useState(false);
	const [isActiveChat, setIsActiveChat] = useState(false);
	const [isNewChat, setIsNewChat] = useState(false);
	const [disabledInputBox, setDisabledInputBox] = useState(false);
	const [isActiveTextInput, setIsActiveTextInput] = useState(null);
	const [quickReplies, setQuickReplies] = useState([]);
	const [chatOffsetHeight, setChatOffsetHeight] = useState(69);
	const [isTyping, setIsTyping] = useState(false);
	const [isChatBotMessage, setIsChatBotMessage] = useState(true);
	const [openSnack, setOpenSnack] = useState(false);
	const [sessionText, setSessionText] = useState(SESSION_EXPIRED_TEXT);
	const [isHandingOff, setIsHandingOff] = useState(false);
	const [userEmail, setUserEmail] = useState('user@kodif.ai');
	const [userName, setUserName] = useState('');
	const [showConnecting, setShowConnecting] = useState(false);
	const [isNativeHandoff, setIsNativeHandoff] = useState(false);
	const isQuickReplyActive = quickReplies.length > 0;
	const classes = useStyles(chatOffsetHeight);
	const [notifications, setNotifications] = useState([]);
	const [retrieving, setRetrieving] = useState(false);
	const [isMessageStreaming, setIsMessageStreaming] = useState(false);
	const [handoffEventsReceived, setHandoffEventsReceived] = useState({
		connected: false,
		firstMessage: false
	});
	const [hasNewMessage, setHasNewMessage] = useState(false);
	const headerRef = useRef(null);
	const containerRef = useRef(null);
	const newMessageRef = useRef(null);
    const [awsWebSocket, setAwsWebSocket] = useState(null);
    const [connectionAttempted, setConnectionAttempted] = useState(false);

	let timer;
	let timerCount = 1;

	useEffect(() => {
		if (isAutopilot) {
			setShowUploadButton(true);
		}
	}, [isAutopilot]);

	useEffect(() => {
		if (playground) {
			if (quickRepliesContainerRef.current !== null) {
				setChatOffsetHeight(quickRepliesContainerRef.current.clientHeight + 25);
			} else if (!isQuickReplyActive) {
				setChatOffsetHeight(69 + 25);
			}
		} else {
			if (quickRepliesContainerRef.current !== null) {
				setChatOffsetHeight(quickRepliesContainerRef.current.clientHeight);
			} else if (!isQuickReplyActive) {
				setChatOffsetHeight(69);
			}
		}
	}, [isQuickReplyActive, quickRepliesContainerRef]);

	useEffect(() => {
		if (conversationId && window.parent) {
			window.parent.postMessage({ type: 'kodif-widget-conversation-id', payload: conversationId }, '*');
		}
	}, [conversationId]);

	const chatbotMessages = useMemo(() => {
		return chatMessages.filter(message => message.senderName === 'Chatbot' && (!message.isChunk || message.isDone));
	}, [chatMessages]);

	useEffect(() => {
		if (window.parent) {
			window.parent.postMessage({ type: 'kodif-widget-chatbot-messages', payload: chatbotMessages.length }, '*');
		}
	}, [chatbotMessages.length]);

	const scrollBottom = (el = document.body) => {
		return (el.scrollTop = el.scrollHeight - el.clientHeight);
	};

	// convert messages from current session to handoff to Gorgeos
	const convertMessageHistory = () => {
		return chatMessages
			.map(({ senderName, value }) => {
				const sender = senderName === 'Chatbot' ? 'BOT' : 'USER';
				return `${sender}: ${value} \n`;
			})
			.join('');
	};

    useEffect(() => {
        const savedConversationId = isTestWidget ? '' : localStorage.getItem('kodifConversationId' + appID) ?? '';

        if (savedConversationId) {
            // Check for handoff data including connecting state
            const handoffData = JSON.parse(localStorage.getItem('kodifHandoff' + appID)) || {};
            if (handoffData.isConnecting) {
                setShowConnecting(true);
            }
        }
    }, [appID, isTestWidget]);

    const setShowConnectingWithStorage = useCallback(
        value => {
            setShowConnecting(value);

            const handoffData = JSON.parse(localStorage.getItem('kodifHandoff' + appID)) || {};

            if (value) {
                handoffData.isConnecting = true;
                localStorage.setItem('kodifHandoff' + appID, JSON.stringify(handoffData));
            } else if (handoffData.isConnecting) {
                delete handoffData.isConnecting;

                if (Object.keys(handoffData).length > 0) {
                    localStorage.setItem('kodifHandoff' + appID, JSON.stringify(handoffData));
                } else {
                    localStorage.removeItem('kodifHandoff' + appID);
                }
            }
        },
        [appID]
    );

    useEffect(() => {
        const handleCustomHandOff = () => {
            setShowConnectingWithStorage(true);
            setTimeout(() => {
                onCustomHandOff(convertMessageHistory(), userEmail, userName);
                onExpandChange(false);
                setIsHandingOff(false);
                setShowConnectingWithStorage(false);
            }, 5000);
        };

        if (isHandingOff === true) {
            handleCustomHandOff();
        }
    }, [isHandingOff, onCustomHandOff, userEmail, userName, onExpandChange, setShowConnectingWithStorage]);

	const collectCookieData = (dataToCollect) => {
		const collectedData = {};

		if (dataToCollect) {
			dataToCollect.forEach(item => {
				let value = null;
				if (item.storage_type === 'LOCAL_STORAGE') {
					value = localStorage.getItem(item.key);
				} else if (item.storage_type === 'COOKIES') {
					value = cookies[item.key];
				}
				if (value) {
					collectedData[item.key] = value;
				}
			});
		}

		return collectedData;
	};

	const resetInput = () => {
		setIsTyping(false);
		setNotifications([]);
		setDisabledInputBox(false);
		setRetrieving(false);
	};

    const connectToChat = async () => {
        const savedConversationId = isTestWidget ? '' : localStorage.getItem('kodifConversationId' + appID) ?? '';

        // Define message queue and processing state here
        let messageQueue = [];
        let messageProcessingInProgress = false; // sort of semaphore...
        let chunkedMessage = '';

        // Declare processMessageQueue here, outside the try block
        function processMessageQueue() {
            if (messageProcessingInProgress || messageQueue.length === 0) {
                return;
            }

            // Block the processing of next messages until the current one is done
            messageProcessingInProgress = true;

            // Poll the next message from the queue
            const message = messageQueue.shift();
            if (chunkedMessage === '') {
                // Enable the typing animation
							setIsTyping(!isNativeHandoff);
            }

            // Delay before processing the message
            setTimeout(() => {
                // Process the message
                if (message.conversation?.respondent === 'CHATBOT') {
                    setIsChatBotMessage(true);
                } else {
                    setIsChatBotMessage(false);
                }

                if (!isAutopilot) {
                    setShowUploadButton(message.content?.showUploadButton);
                }

                if (message.content?.showTextField !== null) {
                    setIsActiveTextInput(Boolean(message.content?.showTextField));
                }

                switch (message.content.type) {
                    case 'quickReply':
                        // eslint-disable-next-line no-case-declarations
                        const newArrMsg = message.content.items.map(obj => {
                            obj.labelLength = obj.label.length;
                            return obj;
                        });
                        // eslint-disable-next-line no-case-declarations
                        setQuickReplies(newArrMsg);
                        resetInput();
                        break;
                    case 'notification':
                        setNotifications(prev => [...prev, message.content.text]);
                        setDisabledInputBox(true);
                        setRetrieving(true);
                        break;
                    case 'done':
                        resetInput();
                        chunkedMessage = '';
                        setChatMessages(setLastMessageAsDone);
                        break;
                    case 'streamEnd':
                        chunkedMessage = '';
                        setChatMessages((prev) => setLastMessageAsDone(prev, message.id));
                        break;
                    default:
                        // eslint-disable-next-line no-case-declarations
                        setIsTyping(false);
                        setNotifications([]);

                        if (message.content?.isChunk) {
                            const isFirstMessage = !chunkedMessage.length;
                            chunkedMessage += message.content.text;

                            if (isFirstMessage) {
                                const convertedMessage = convertMessage(message);
                                setChatMessages(currentChatMessages => [...currentChatMessages, convertedMessage]);
                            } else {
                                message.content.text = chunkedMessage;
                                const convertedMessage = convertMessage(message);
                                setChatMessages(current => [...current.slice(0, -1), convertedMessage]);
                            }
                        } else {
                            setChatMessages(currentChatMessages => [...currentChatMessages, convertMessage(message)]);
                            // Disable the typing animation after processing the message
                            resetInput();
                            break;
                        }
                }

                // If this was a new chat, set isNewChat to false now that the first message is processed
                if (isNewChat) {
                    setIsNewChat(false);
                }

                // Unblock the queue and continue
                messageProcessingInProgress = false;
                processMessageQueue();
            }, message.content?.delayBeforeShowMs || 0);
        }

        try {
            // Track that we've attempted a connection to prevent duplicate connections
            if (connectionAttempted) {
                console.log('Connection already attempted, skipping duplicate connection attempt');
                return;
            }

            setConnectionAttempted(true);

            // Don't create a new connection if one already exists and is open/connecting
            if (
                awsWebSocket &&
                (awsWebSocket.readyState === WebSocket.OPEN || awsWebSocket.readyState === WebSocket.CONNECTING)
            ) {
                console.log('WebSocket connection already exists, skipping creation');
                return;
            }

            if (!savedConversationId) {
                setIsTyping(isNativeHandoff ? false : true);
            }
            setIsActiveChat(false);

            const ws = new WebSocket(`${AWS_WEB_SOCKET_URL}?conversationId=${conversationId}`);

            ws.onopen = async () => {
                console.log('AWS WebSocket connection established');
                ws.send(
                    JSON.stringify({
                        action: 'startConversation',
                        appId: appID,
                        conversationId: conversationId,
                        flowVersion: flowVersionUuid,
                        collectedData: JSON.stringify(
                            collectCookieData(JSON.parse(localStorage.getItem('chatConfig'))?.dataToCollect)
                        ),
                        policyId: policyId,
                        userEmail: policyTestUserEmail,
                        isTestWidget: isTestWidget,
                        localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        userLanguage: i18n.language
                    })
                );

                // Clear existing timer first to prevent duplicate timers
                if (timer) {
                    clearInterval(timer);
                }

                // Start ping interval to keep connection alive
                timer = setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN && conversationId && timerCount > 0) {
                        timerCount++;
                        ws.send(
                            JSON.stringify({
                                action: 'ping',
                                appId: appID,
                                conversationId: conversationId
                            })
                        );
                    }
                }, 10000);

                setIsActiveChat(true);
                setAwsWebSocket(ws);
            };

            // Handle WebSocket messages
            ws.onmessage = async event => {
                try {
                    if (event?.data === null || event?.data === undefined || event?.data === '') {
                        return;
                    }

                    const data = JSON.parse(event.data);

                    if (data.action) {
                        if (data.action === 'pong') {
                            const eventBody = data.body;

                            if (eventBody === 'SESSION_EXPIRED' || eventBody === 'SESSION_END') {
                                setSessionText('Your session has expired');
                                setIsTyping(false);
                                setOpenSnack(true);
                                setDisabledInputBox(true);
                                setRetrieving(false);
                                clearInterval(timer);
                                localStorage.removeItem('kodifConversationId' + appID);
                                localStorage.removeItem('kodifHandoff' + appID);
                                timerCount = 0;
                                ws.close();
                                scrollBottom(chatWindowRef.current);
                            }
                            if (eventBody === 'SESSION_RESTART') {
                                timerCount = 1;
                                localStorage.removeItem('kodifHandoff' + appID);
                            }
                            if (eventBody === 'DISABLE_TEXT_FIELD') {
                                setIsActiveTextInput(false);
                                setIsTyping(false);
                            }
                            if (eventBody === 'ENABLE_TEXT_FIELD') {
                                setIsActiveTextInput(true);
                            }
                            if (eventBody === 'NATIVE_HANDOFF_STARTED') {
                                setShowConnectingWithStorage(true);
                                setIsNativeHandoff(true);
                            }
                            if (typeof eventBody === 'object' && eventBody.type === 'NATIVE_HANDOFF_CONNECTED') {
                                const handoffData = {
                                    agentName: eventBody.agentName,
                                    agentPicture: eventBody.agentPicture,
                                    isConnecting: true // Keep the connecting flag when agent connects
                                };
                                localStorage.setItem('kodifHandoff' + appID, JSON.stringify(handoffData));
                                setHandoffEventsReceived(prev => {
                                    const newState = { ...prev, connected: true };

                                    if (newState.firstMessage) {
                                        setShowConnectingWithStorage(false);
                                        setIsActiveTextInput(true);
                                    }

                                    return newState;
                                });
                            }
                            if (eventBody === 'NATIVE_HANDOFF_FIRST_AGENT_MESSAGE') {
                                setHandoffEventsReceived(prev => {
                                    const newState = { ...prev, firstMessage: true };

                                    if (newState.connected) {
                                        setShowConnectingWithStorage(false);
                                        setIsActiveTextInput(true);
                                    }

                                    return newState;
                                });
                            }
                            if (typeof eventBody === 'object' && HANDOFF_TYPES.includes(eventBody.type)) {
                                setUserEmail(eventBody.email);
                                setUserName(eventBody.name);
                                setIsHandingOff(true);
                            }
                        }
                        else if (data.action === 'message') {
                            const message = data.body;
                            messageQueue.push(message);
                            processMessageQueue();
                        }
                    }
                    // If no type property, check if it's a direct message with content field
                    else if (data.body) {
                        // This is a direct message without wrapper
                        messageQueue.push(data);
                        processMessageQueue();
                    }
                } catch (error) {
                    console.error('Error processing WebSocket message:', error);
                }
            };

            ws.onerror = error => {
                console.error('WebSocket error:', error);
                setIsActiveChat(false);
            };
        } catch (e) {
            console.warn('Error connecting to chat:', e);
            setConnectionAttempted(false);
        }
    };

	const retrieveChatMessages = async (callback) => {
		const savedConversationId = isTestWidget ? '' : localStorage.getItem('kodifConversationId' + appID) ?? '';
		const isMinimized = localStorage.getItem('kodifMinimized' + appID) !== 'false';

		if (!savedConversationId) return;

		setDisabledInputBox(true);
		setRetrieving(true);

		await axios
			.get(`${BASE_URL}/api/v1/${appID}/conversation/${savedConversationId}`)
			.then(({ data }) => {
				if (data.success && data.payload !== null) {
					setConversationId(data.payload.id);

					if (!_.isEmpty(data.payload.messages)) {

						const sortMessages = data.payload.messages
							.filter(message => message.content.type !== 'quickReply')
							.sort((d1, d2) => new Date(d1.createdAt).getTime() - new Date(d2.createdAt).getTime());
						const prevMessages = sortMessages.map(message => convertMessage(message));
						setChatMessages(prevMessages);

						const lastMessage = _.last(data.payload.messages);
						if (lastMessage.content.type === 'quickReply' && lastMessage.user.type === 'business') {
							const newArrMsg = lastMessage.content.items.map(obj => {
								obj.labelLength = obj.label.length;
								return obj;
							});
							setQuickReplies(newArrMsg);
							setIsActiveTextInput(Boolean(lastMessage.content?.showTextField));
						}

						setShowUploadButton(isAutopilot ? true : lastMessage.content?.showUploadButton);

						if (!isMinimized) {
							setTimeout(() => onExpandChange(true), 100);
						}
					}
				} else {
					throw new Error('couldn\'t fetch the conversation');
				}
			}).catch(() => {
				onRestartConversation();
			}).finally(() => {
				setDisabledInputBox(false);
				setRetrieving(false);
				callback();
			});
	};

	const {tabBlurred} = useSuspendChatConnection(retrieveChatMessages, isChatWidgetVisible);

	const startConversationData = async () => {
		try {
			const savedConversationId = isTestWidget ? '' : localStorage.getItem('kodifConversationId' + appID) ?? '';
			let urlConv = `${BASE_URL}/api/v1/${appID}/conversation`;

			if (savedConversationId) {
				urlConv = `${BASE_URL}/api/v1/${appID}/conversation/${savedConversationId}`;
			}

			await axios
				.get(urlConv)
				.then(({ data }) => {
					if (data.success && data.payload !== null) {
                        if (awsWebSocket) {
                            console.log('Closing existing WebSocket before setting new conversation');
                            awsWebSocket.close();
                            setAwsWebSocket(null);
                        }

						setConversationId(data.payload.id);

						if (!_.isEmpty(data.payload.messages)) {
							const sortMessages = data.payload.messages
								.filter(message => message.content.type !== 'quickReply')
								.sort((d1, d2) => new Date(d1.createdAt).getTime() - new Date(d2.createdAt).getTime());
							const prevMessages = sortMessages.map(message => convertMessage(message));
							setChatMessages(prevMessages);

							const lastMessage = _.last(data.payload.messages);
							if (lastMessage.content.type === 'quickReply' && lastMessage.user.type === 'business') {
								const newArrMsg = lastMessage.content.items.map(obj => {
									obj.labelLength = obj.label.length;
									return obj;
								});
								setQuickReplies(newArrMsg);
							}
							if (!isAutopilot) {
								setShowUploadButton(isAutopilot ? true : lastMessage.content?.showUploadButton);
							}
						}
					} else {
						throw new Error('couldn\'t fetch the conversation');
					}
				})
				.catch(e => {
					if (e.response?.data?.errorMessage === 'SESSION_RESTART') {
						axios.get(`${BASE_URL}/api/v1/${appID}/conversation`).then(({ data }) => {
							if (data.success && data.payload !== null) {
                                if (awsWebSocket) {
                                    awsWebSocket.close();
                                    setAwsWebSocket(null);
                                }

								setConversationId(data.payload.id);

								if (!_.isEmpty(data.payload.messages)) {
									const sortMessages = data.payload.messages
										.filter(message => message.content.type !== 'quickReply')
										.sort((d1, d2) => new Date(d1.createdAt).getTime() - new Date(d2.createdAt).getTime());
									const prevMessages = sortMessages.map(message => convertMessage(message));
									setChatMessages(prevMessages);

									const lastMessage = _.last(data.payload.messages);
									if (lastMessage.content.type === 'quickReply' && lastMessage.user.type === 'business') {
										const newArrMsg = lastMessage.content.items.map(obj => {
											obj.labelLength = obj.label.length;
											return obj;
										});
										setQuickReplies(newArrMsg);
									}

									setShowUploadButton( isAutopilot ? true : lastMessage.content?.showUploadButton);
								}
							}
						});
					}
				})
				.finally(() => {
					setIsActiveChat(true);
					setDisabledInputBox(false);
					setRetrieving(false);
				});
			timerCount = 1;
		} catch (e) {
			console.warn(e);
		}
	};

	const initialized = useRef(false);

	useEffect(() => {
		if (isExpanded && !initialized.current) {
			void startConversationData();
			initialized.current = true;
		}

		if (isExpanded && chatbotMessages.length > 0 && chatWindowRef.current) {
			scrollBottom(chatWindowRef.current);
		}
	}, [isExpanded]);

	useEffect(() => {
		if (conversationId !== null) {
			const cookieId = localStorage.getItem('kodifConversationId' + appID);
			if (conversationId !== cookieId) {
				localStorage.setItem('kodifConversationId' + appID, conversationId);
			}
		

            setConnectionAttempted(false);

            if (!awsWebSocket || awsWebSocket.readyState !== WebSocket.OPEN) {
                void connectToChat();
            }
        }

		return () => {};
	}, [conversationId]);

	const onUserMessageChange = e => {
		setUserMessage(e.target.value);
		containerRef.current.focus();
	};

	const debouncedOnUserMessageChange = useCallback(_.debounce(onUserMessageChange, 50), []);

	useEffect(() => {
		return () => {
			if (awsWebSocket) {
                awsWebSocket.close();
            }
			clearInterval(timer);
			debouncedOnUserMessageChange.cancel();
		};
	}, []);

	useEffect(() => {
		if (chatWindowRef?.current) {
			chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
		}
	}, [chatMessages, isQuickReplyActive, chatOffsetHeight, isTyping]);

	useEffect(() => {
		if (!_.isEmpty(fileUploadRef?.current?.value)) {
			setShowPreviewImg(true);
			setTimeout(() => scrollBottom(chatWindowRef.current), 300);
		} else {
			setShowPreviewImg(false);
			setUserUpdFile('');
			setSubmitType('text');
		}
	}, [fileUploadRef?.current?.value]);

    useEffect(() => {
        if (awsWebSocket && awsWebSocket.readyState === WebSocket.OPEN) {
            setDisabledInputBox(false);
            setRetrieving(false);
        }
    }, [awsWebSocket]);

	const onUserMessageSubmit = (newMessage = userMessage) => {
		try {
			if (newMessage.trim().length > 0 && awsWebSocket && awsWebSocket.readyState === WebSocket.OPEN) {
				if (isQuickReplyActive) {
					setQuickReplies([]);
				}
				if (isChatBotMessage) {
					setNotifications([]);
					setIsTyping(true);
					setDisabledInputBox(true);
				}
				const newUserMessage = { ...DEFAULT_USER_MESSAGE_STATE };
				newUserMessage.value = newMessage;
				newUserMessage.time = moment().format('hh:mm a');
				setChatMessages(currentChatMessages => [...currentChatMessages, newUserMessage]);
				
                awsWebSocket.send(
                    JSON.stringify({
                        action: 'message',
                        appId: appID,
                        conversationId: conversationId,
                        message: {
                            conversation: { id: conversationId },
                            user: HARDCODED_USER,
                            content: {
                                type: 'text',
                                text: newMessage
                            }
                        }
                    })
                );

				if (newMessage === userMessage) {
					setUserMessage('');
					if (textInputRef.current) {
						textInputRef.current.value = '';
					}
				}
			}
		} catch (e) {
			console.error('[error]', e);
		}
	};

	const onUserFileSubmit = async (newFile = userUpdFile, newText = userMessage) => {
		try {
			if (newFile.length > 0 && awsWebSocket && awsWebSocket.readyState === WebSocket.OPEN) {
				if (isQuickReplyActive) {
					setQuickReplies([]);
				}
				if (isChatBotMessage) {
					setNotifications([]);
					setIsTyping(true);
					setDisabledInputBox(true);
				}

				const newUserFile = { ...DEFAULT_USER_MESSAGE_FILE_STATE };
				newUserFile.encodedAttachment = newFile;
				newUserFile.value = newText;
				newUserFile.time = moment().format('hh:mm a');
				setChatMessages(currentChatMessages => [...currentChatMessages, newUserFile]);
				
                // --- Chunking Logic ---
                const CHUNK_SIZE = 30 * 1024; // 30KB chunk size
                const totalSize = newFile.length;
                const totalChunks = Math.ceil(totalSize / CHUNK_SIZE);
                const fileId = `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

                // 1. Send file start message
                awsWebSocket.send(
                    JSON.stringify({
                        action: 'fileUpload',
                        appId: appID,
                        conversationId: conversationId,
                        payload: {
                            step: 'start',
                            user: HARDCODED_USER,
                            text: newText,
                            fileInfo: {
                                id: fileId,
                                totalSize: totalSize,
                                totalChunks: totalChunks
                            }
                        }
                    })
                );

                // 2. Send file chunks
                for (let i = 0; i < totalChunks; i++) {
                    const start = i * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, totalSize);
                    const chunkData = newFile.substring(start, end);

                    await new Promise(resolve => setTimeout(resolve, 10));

                    awsWebSocket.send(
                        JSON.stringify({
                            action: 'fileUpload',
                            appId: appID,
                            conversationId: conversationId,
                            payload: {
                                step: 'chunk',
                                user: HARDCODED_USER,
                                fileInfo: {
                                    id: fileId,
                                    chunkIndex: i
                                },
                                chunkData: chunkData
                            }
                        })
                    );
                }

                await new Promise(resolve => setTimeout(resolve, 10));

                // 3. Send file end message
                awsWebSocket.send(
                    JSON.stringify({
                        action: 'fileUpload',
                        appId: appID,
                        conversationId: conversationId,
                        payload: {
                            step: 'end',
                            user: HARDCODED_USER,
                            fileInfo: {
                                id: fileId
                            }
                        }
                    })
                );
                // --- End Chunking Logic ---

				setUserMessage('');
				setUserUpdFile('');
				setSubmitType('text');
				if (textInputRef.current) {
					textInputRef.current.value = '';
				}
				fileUploadRef.current.value = '';
			}
		} catch (e) {
			console.error('[error] Failed to send file:', e);
            setUserMessage('');
            setUserUpdFile('');
            setSubmitType('text');
            if (textInputRef.current) {
                textInputRef.current.value = '';
            }
            if (fileUploadRef.current) {
                fileUploadRef.current.value = '';
            }
            setNotifications([]);
            setIsTyping(false);
            setDisabledInputBox(false);
		}
	};

	const trackLinkClick = async ({ linkUrl, linkTitle }) => {
		if (IS_DEVELOPMENT) return;

		const trackingData = {
			applicationId: appID,
			conversationId,
			linkUrl,
			linkTitle,
			...pageSpecificData,
		};
		void axios.post(`${BASE_URL}/api/v1/track-link-click`, trackingData);
	};

	const onQuickReplyClick = ({ value }) => {
		if (isActiveTextInput === null) {
			setDisabledInputBox(true);
		} else {
			setDisabledInputBox(false);
			setRetrieving(false);
		}
		onUserMessageSubmit(value);
		setQuickReplies([]);
		setOpenSnack(false);
	};

	const onEndChat = async () => {
		localStorage.removeItem('kodifConversationId' + appID);
		localStorage.removeItem('kodifHandoff' + appID);
        if (isActiveChat && awsWebSocket) {
            awsWebSocket.close();
		}
		setIsCloseMenuActive(false);
		onExpandChange(false);
		setOpenSnack(false);
		setChatMessages([]);
	};

	const onNewChat = async () => {
		if (isQuickReplyActive) {
			setQuickReplies([]);
		}
		setIsNewChat(true);
		setDisabledInputBox(true);
		setOpenSnack(false);
		setIsNativeHandoff(false);
		setHandoffEventsReceived({ connected: false, firstMessage: false });
        setConnectionAttempted(false);
        setShowConnectingWithStorage(false);

        if (awsWebSocket) {
            awsWebSocket.close();
            setAwsWebSocket(null);
        }

        if (timer) {
            clearInterval(timer);
            timer = null;
        }

		setIsCloseMenuActive(false);
		setChatMessages([]);
		setQuickReplies([]);
		setConversationId(null);
		localStorage.removeItem('kodifConversationId' + appID);
		localStorage.removeItem('kodifMinimized' + appID);
		localStorage.removeItem('kodifHandoff' + appID);

        setTimeout(async () => {
			await startConversationData();
		}, 100);
	};

	const onRestartConversation = () => {
		if (!isNewChat) {
			void onNewChat();
		}
	};

	useEffect(() => {
		const handleMessage = (event) => {
			if (event.data.type === 'restart-conversation') {
				onRestartConversation();
			}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, []);

	const renderChatMessages = () => {
		let isTimeEnabled = true;
		return chatMessages.map((message, i) => {
			const nextMessageType = chatMessages.length > i + 1 ? chatMessages[i + 1].type : null;
			isTimeEnabled = nextMessageType !== message.type;

			// Get handoff data from localStorage
			const handoffData = JSON.parse(localStorage.getItem('kodifHandoff' + appID)) || {};

			return (
				<ChatMessage
					appID={appID}
					conversationId={conversationId}
					companyConfig={companyConfig}
					key={`ChatMessage-${i}`}
					message={message}
					isTimeEnabled={isTimeEnabled}
					playground={playground}
					isAutopilot={isAutopilot}
					chatWindowRef={chatWindowRef}
					setIsMessageStreaming={setIsMessageStreaming}
					agentName={handoffData.agentName}
					agentPicture={handoffData.agentPicture}
					trackLinkClick={trackLinkClick}
				/>
			);
		});
	};

	const renderChatRating = () => {
		if (!companyConfig.isCsatEnabled) return null;

		const lastMessage = chatMessages[chatMessages.length - 1];

		if (!lastMessage) return null;

		const hasUserInput = chatMessages.some(message => message.type === 'user');
		const isBusinessMessage = lastMessage.type === 'business';
		const messageIsNotChunkOrIsDone = (!lastMessage.isChunk || lastMessage.isDone);
		const needToRenderRating = hasUserInput && isBusinessMessage && messageIsNotChunkOrIsDone && !isMessageStreaming && !openSnack;

		return needToRenderRating ? <ChatRating conversationId={conversationId} lastMessage={lastMessage} companyConfig={companyConfig}/> : null;
	};

	let classNameBubble = classes.quickRepliesBoxContainer;

	if (quickReplies?.length > 3) {
		classNameBubble = classes.quickRepliesBoxContainerMini;
	}

	useEffect(() => {
		const existingElement = document.getElementById('kodifPlaygroundStyles');
		if (playground && !existingElement) {
			const styleElement = document.createElement('link');
			styleElement.href = '/assets/css/chat.css';
			styleElement.rel = 'stylesheet';
			document.head.appendChild(styleElement);
		}

		return () => {
			if (existingElement) {
				existingElement.remove();
			}
		};
	}, [playground]);

	useEffect(() => {
		if (chatMessages[chatMessages.length - 1] && !isTyping) {
			setHasNewMessage(true);
			const timeout = setTimeout(() => {
				setHasNewMessage(false);
			}, 3000);

			return () => clearTimeout(timeout);
		}
	}, [chatMessages, isTyping]);

	useEffect(() => {
		if (hasNewMessage && newMessageRef?.current) {
			newMessageRef?.current?.focus();
		}
	}, [hasNewMessage]);

	function FooterBlock() {
		if (playground) {
			return (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					<PoweredByKodifLogo />
				</div>
			);
		}
		return <PoweredByKodifLogo />;
	}

	if (!isExpanded) {
		return null;
	}

	if (!isActiveChat) {
		return (
			<FocusTrapWithVisibilityCheck isActive={isExpanded} containerRef={containerRef}>
				<div
					className={`${
						playground ? classes.chatContainerPlayground : isMobile ? classes.chatContainerMobile : classes.chatContainer
					}`}
					style={{
						background: companyConfig.brandColor,
						margin: `${appPosition === 'right' ? '0 0 0 auto' : '0 auto 0 0'}`,
						boxShadow: `boxShadow: '0px 5px 31px ${companyConfig?.brandColor}`,
						width: isTestWidget ? '100%' : undefined,
					}}
					ref={containerRef}
				>
					<header className={classes.chatHeader} ref={headerRef} style={{ display: extension && 'none' }}>
						<div className={classes.chartHeaderLogoContainer}>
							<img
								src={companyConfig?.chatLogo}
								alt=""
								tabIndex={0}
								style={{ maxWidth: 170, maxHeight: 45, overflow: 'hidden' }}
							/>
							{!_.isEmpty(companyConfig?.name) && (
								<Typography
									marginLeft="10px"
									marginTop="5px"
									color={companyConfig.headerTitleColor || 'white'}
									lineHeight="normal"
								>
									{companyConfig.name}
								</Typography>
							)}
						</div>
						{(!playground && !isTestWidget) && (
							<div className={classes.chartHeaderLogoContainer} tabIndex={0}>
								<CloseButton fill={companyConfig?.backgroundColor} onClick={() => setIsCloseMenuActive(true)} />
							</div>
						)}
					</header>
					<div className={classes.chatContentContainer} style={{ height: extension && '100%', boxShadow: 'none' }}>
						<div
							className={classes.chatLoading}
							style={{
								background: companyConfig.brandColor
							}}
						></div>
						<ChatInputBox
							onUserMessageSubmit={onUserMessageSubmit}
							debouncedOnUserMessageChange={debouncedOnUserMessageChange}
							textInputRef={textInputRef}
							fileUploadRef={fileUploadRef}
							submitType={submitType}
							setSubmitType={setSubmitType}
							setUserUpdFile={setUserUpdFile}
							showUploadButton={showUploadButton}
							disableWindowBlurListener
							isNewChat={isNewChat}
							disabledInputBox={retrieving || isNewChat || isTyping || isActiveTextInput === false}
							playground={playground}
							companyConfig={companyConfig}
							setUserMessage={setUserMessage}
							userMessage={userMessage}
							userFileMessage={userFileMessage}
							setUserFileMessage={setUserFileMessage}
							onUserFileSubmit={onUserFileSubmit}
							isMobile={isMobile}
							retrieving={retrieving}
						/>
						{!hideFooter && (
							<div className={classes.chatBoxFooter} style={{ display: extension && 'none' }}>
								<FooterBlock />
							</div>
						)}
					</div>

					{isCloseMenuActive && (
						<ChatCloseConfirmation
							companyConfig={companyConfig}
							fill={companyConfig?.headerIconColor || 'white'}
							onCancel={() => setIsCloseMenuActive(false)}
							onEndChat={onEndChat}
						/>
					)}
				</div>
			</FocusTrapWithVisibilityCheck>
		);
	}

	const showHideTextBox = () => {
		if (!openSnack) {
			if (isActiveTextInput === null && !isQuickReplyActive) {
				return (
					<ChatInputBox
						companyConfig={companyConfig}
						textInputRef={textInputRef}
						onUserMessageSubmit={onUserMessageSubmit}
						debouncedOnUserMessageChange={debouncedOnUserMessageChange}
						fileUploadRef={fileUploadRef}
						submitType={submitType}
						setSubmitType={setSubmitType}
						setUserUpdFile={setUserUpdFile}
						showUploadButton={showUploadButton}
						isNewChat={isNewChat}
						disabledInputBox={retrieving || isNewChat || isTyping || isActiveTextInput === false}
						playground={playground}
						setUserMessage={setUserMessage}
						userMessage={userMessage}
						userFileMessage={userFileMessage}
						setUserFileMessage={setUserFileMessage}
						onUserFileSubmit={onUserFileSubmit}
						isMobile={isMobile}
						retrieving={retrieving}
					/>
				);
			}
			if (isActiveTextInput) {
				return (
					<ChatInputBox
						sessionText={sessionText}
						companyConfig={companyConfig}
						onUserMessageSubmit={onUserMessageSubmit}
						debouncedOnUserMessageChange={debouncedOnUserMessageChange}
						textInputRef={textInputRef}
						fileUploadRef={fileUploadRef}
						submitType={submitType}
						setSubmitType={setSubmitType}
						setUserUpdFile={setUserUpdFile}
						showUploadButton={showUploadButton}
						isNewChat={isNewChat}
						disabledInputBox={retrieving || isNewChat || isTyping || isActiveTextInput === false}
						playground={playground}
						setUserMessage={setUserMessage}
						userMessage={userMessage}
						userFileMessage={userFileMessage}
						setUserFileMessage={setUserFileMessage}
						onUserFileSubmit={onUserFileSubmit}
						isMobile={isMobile}
						retrieving={retrieving}
					/>
				);
			}
			return null;
		}
		return null;
	};

	return (
		<FocusTrapWithVisibilityCheck isActive={isExpanded} containerRef={containerRef}>
			<div
				className={`${
					playground ? classes.chatContainerPlayground : isMobile ? classes.chatContainerMobile : classes.chatContainer
				}`}
				style={{
					background: companyConfig.brandColor,
					margin: `${appPosition === 'right' ? '0 0 0 auto' : '0 auto 0 0'}`,
					height: (playground || isTestWidget) ? '100%' : isMobile ? maxHeight : maxHeight - 130,
					width: isTestWidget ? '100%' : undefined,
					maxHeight: (playground || isTestWidget) ? '100%' : isMobile ? maxHeight : 700,
				}}
				ref={containerRef}
			>
				{showConnecting && (
					<HandoffMessage customMessage={JSON.parse(localStorage.getItem('chatConfig'))?.handoffMessage} />
				)}
				{!hideHeader && (
					<header className={classes.chatHeader} ref={headerRef} style={{ display: extension && 'none' }}>
						<div className={classes.chartHeaderLogoContainer} tabIndex="0" autoFocus
						>
							<img
								src={companyConfig?.chatLogo}
								alt=""
								style={{ maxWidth: 170, maxHeight: 45, overflow: 'hidden' }}
							/>
							{!_.isEmpty(companyConfig?.name) && (
								<Typography
									marginLeft="10px"
									marginTop="5px"
									color={companyConfig.headerTitleColor || 'white'}
									lineHeight="normal"
								>
									{companyConfig.name}
								</Typography>
							)}
							<span className="visually-hidden">{t('header.title')}</span>
						</div>
						{!isTestWidget && (
							<Tooltip
								title={t('header.new')}
								sx={{
									top: 0
								}}
							>
								<button
									onClick={onRestartConversation}
									onTouchStart={onRestartConversation} // Ensure touch events are handled
									className={classes.chartHeaderRefreshIcon}
									style={{ opacity: isNewChat ? 0.5 : 1 }}
								>
									<RefreshIcon width={20} fill={companyConfig?.headerIconColor || 'white'} />
								</button>
							</Tooltip>
						)}
						{(!playground && !isTestWidget) && (
							<button aria-label={t('header.close')} className={classes.chartHeaderMinimize} onClick={() => onExpandChange(false)}>
								<MinimizeBtn fill={companyConfig?.headerIconColor || 'white'} />
							</button>
						)}
					</header>
				)}
				<div
					className={playground ? classes.chatContentContainerPlayground : classes.chatContentContainer}
					style={{
						height: containerHeight,
						display: 'flex', // Maintain flex display
						borderRadius: isMobile || playground ? '0' : '0 0 16px 16px'
					}}
				>
					{isNewChat && (
						<div
							className={classes.chatLoading}
							style={{
								background: companyConfig.brandColor
							}}
						></div>
					)}
					{!isNewChat && (
						<>
							<div
								className={playground ? classes.chatContentPlayground : classes.chatContent}
								ref={chatWindowRef}
								// style={{ height: `${521 - chatOffsetHeight}px` }}
							>
								{renderChatMessages()}
								{renderChatRating()}
								{showPreviewImg && (
									<AlertFile
										setShowPreviewImg={setShowPreviewImg}
										fileUploadRef={fileUploadRef}
										fileImg={userUpdFile}
										fileUrl={fileUploadRef.current.value}
									/>
								)}
								{isChatBotMessage && isTyping && (
									<ChatMessageTyping
										companyConfig={companyConfig}
										notifications={notifications}
										setNotifications={setNotifications}
										isAutopilot={isAutopilot}
									/>
								)}
								{!_.isEmpty(userFileMessage) && userFileMessage !== 'Loading' && (
									<Alert severity="warning" icon={false} className={classes.fileMessageInfo}>
										{userFileMessage}
									</Alert>
								)}
								{isQuickReplyActive && !isTyping && (
									<div ref={quickRepliesContainerRef} className={classNameBubble} style={{ width: '100%' }}>
										{quickReplies.map((reply, i) => (
											<ChatQuickReply
												companyConfig={companyConfig}
												quickRepliesCount={quickReplies?.length || 0}
												key={`reply-${i}`}
												reply={reply}
												onQuickReplyClick={onQuickReplyClick}
											/>
										))}
									</div>
								)}
							</div>
						</>
					)}
					{hasNewMessage && <div aria-live="assertive" role="status" className="visually-hidden" tabIndex="-1" ref={newMessageRef}>
						{t('newMessage')}
					</div>}
					{showHideTextBox()}
					{!hideFooter && (
						<div
							className={playground ? classes.chatBoxFooterPlayground : classes.chatBoxFooter}
							style={{ display: extension && 'none' }}
						>
							<FooterBlock />
						</div>
					)}
					{openSnack && (
						<ChatCSAT
							companyConfig={companyConfig}
							onNewChat={onNewChat}
							sessionText={sessionText}
							onEndChat={onEndChat}
						/>
					)}
				</div>
			</div>
		</FocusTrapWithVisibilityCheck>
	);
}
